// import { LoadingScreen } from "@Components/Base";
import { CircularProgress, Stack } from "@mui/material";
import { lazyRouteComponent } from "@tanstack/react-router";
import { ComponentType, FC, LazyExoticComponent, Suspense } from "react";
import { JSX } from "react/jsx-runtime";

type LoadableComponent<P> =
  | LazyExoticComponent<ComponentType<P>>
  | ComponentType<P>;

export const Loadable = <P extends object>(
  Component: LoadableComponent<P>,
): FC<P> => {
  const LoadableComponent: FC<P> = (props: JSX.IntrinsicAttributes & P) => {
    return (
      <Suspense
        fallback={
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };

  const name =
    // @ts-expect-error lazy için displayName var ama tipte yok
    Component.displayName || Component.name || "Component";
  LoadableComponent.displayName = `Loadable(${name})`;

  return LoadableComponent;
};

export const NotFoundPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Errors/NotFound.page")),
);

export const HomePage = Loadable(
  lazyRouteComponent(() => import("@Pages/Home/Home.page")),
);

export const LoginPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Auth/Login.page")),
);

// ** Corporation Pages ** //

export const CorporationListPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Corporation/CorporationList.page")),
);

export const CorporationDetailPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Corporation/CorporationDetail.page")),
);

export const CorporationProductListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Corporation/CorporationProductList.page"),
  ),
);

export const CorporationPersonnelListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Corporation/CorporationPersonnelList.page"),
  ),
);

export const CorporationChatBotPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Corporation/CorporationChatBot.page"),
  ),
);

// ** Financial Pages ** //

export const FinancialProductTransactionListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/ProductTransaction/FinancialProductTransactionList.page"
      ),
  ),
);

export const FinancialProductTransactionDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/ProductTransaction/FinancialProductTransactionDetail.page"
      ),
  ),
);

export const FinancialTimestampTransactionListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/TimestampTransaction/FinancialTimestampTransactionList.page"
      ),
  ),
);

export const FinancialTimestampTransactionReportListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/TimestampTransaction/FinancialTimestampTransactionReportList.page.tsx"
      ),
  ),
);

export const FinancialTimestampTransactionCorporationListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/TimestampTransaction/FinancialTimestampTransactionCorporationList.page"
      ),
  ),
);

export const FinancialTimestampTransactionDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/TimestampTransaction/FinancialTimestampTransactionDetail.page"
      ),
  ),
);

export const FinancialPaymentTypeListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Financial/PaymentType/FinancialPaymentTypeList.page"),
  ),
);

export const FinancialPaymentTypeDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import("@Pages/Financial/PaymentType/FinancialPaymentTypeDetail.page"),
  ),
);

export const FinancialMembershipTypeListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/MembershipType/FinancialMembershipTypeList.page"
      ),
  ),
);

export const FinancialMembershipTypeDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/Financial/MembershipType/FinancialMembershipTypeDetail.page"
      ),
  ),
);

export const FinancialExchangeRateListPage = Loadable(
  lazyRouteComponent(
    () =>
      import("@Pages/Financial/ExchangeRate/FinancialExchangeRateList.page"),
  ),
);

export const FinancialExchangeRateDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import("@Pages/Financial/ExchangeRate/FinancialExchangeRateDetail.page"),
  ),
);

// ** Product Pages ** //

export const ProductProductsListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/Products/ProductProductsList.page"),
  ),
);

export const ProductProductsDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/Products/ProductProductsDetail.page"),
  ),
);

export const ProductProductPriceDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/Products/ProductPoductsPriceDetail.page"),
  ),
);

export const ProductProductPricesListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/ProductPrices/ProductProductPricesList.page"),
  ),
);

export const ProductProductPricesDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import("@Pages/Product/ProductPrices/ProductProductPricesDetail.page"),
  ),
);

export const ProductUserScaleListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/UserScale/ProductUserScaleList.page"),
  ),
);

export const ProductUserScaleDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Product/UserScale/ProdcutUserScaleDetail.page"),
  ),
);

// ** Cloud Pages ** //

export const CloudRoleTemplateListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/RoleTemplate/CloudRoleTemplateList.page"),
  ),
);

export const CloudRoleTemplateDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/RoleTemplate/CloudRoleTemplateDetail.page"),
  ),
);

export const CloudPermissionDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/Permission/PermissionDetailPage.tsx"),
  ),
);

export const CloudLanguageListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/Language/CloudLanguageList.page"),
  ),
);

export const CloudLanguageDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/Language/CloudLanguageDetail.page"),
  ),
);

export const CloudChatBotListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/ChatBot/CloudChatBotList.page"),
  ),
);

export const CloudChatBotDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/ChatBot/CloudChatBotDetail.page"),
  ),
);

export const CloudChatBotReportPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/ChatBot/CloudChatBotReport.page"),
  ),
);

export const CloudPartnerListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/Partner/CloudPartnerList.page"),
  ),
);

export const CloudPartnerDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Cloud/Partner/CloudPartnerDetail.page"),
  ),
);

// ** Contact Pages ** //

export const ContactListPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Contact/ContactList.page")),
);

export const ContactDetailPage = Loadable(
  lazyRouteComponent(() => import("@Pages/Contact/ContactDetail.page")),
);

// ** Support Ticket Pages ** //

export const SupportTicketListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/SupportTicket/SupportTicketList.page"),
  ),
);

export const SupportTicketDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/SupportTicket/SupportTicketDetail.page"),
  ),
);

// ** User Management Pages ** //

export const UserManagementUserListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/UserManagement/User/UserManagementUserList.page"),
  ),
);

export const UserManagementUserDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/UserManagement/User/UserManagementUserDetail.page"),
  ),
);

export const UserManagementRoleListPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/UserManagement/Role/UserManagementRoleList.page"),
  ),
);

export const UserManagementRoleDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/UserManagement/Role/UserManagementRoleDetail.page"),
  ),
);

export const UserManagementPermissionListPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/UserManagement/Permission/UserManagementPermissionList.page"
      ),
  ),
);

export const UserManagementPermissionDetailPage = Loadable(
  lazyRouteComponent(
    () =>
      import(
        "@Pages/UserManagement/Permission/UserManagementPermissionDetail.page"
      ),
  ),
);

// ** Gamification Pages ** //

export const GamificationDetailPage = Loadable(
  lazyRouteComponent(
    () => import("@Pages/Gamification/GamificationDetailPage"),
  ),
);
